import { HttpClient } from '@angular/common/http';
import {
  EnvironmentProviders,
  Provider,
  inject,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  Routes,
  provideRouter,
  withComponentInputBinding,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withRouterConfig,
} from '@angular/router';
import { map, switchMap } from 'rxjs';

import {
  provideMobiUiIconBundleSupport,
  provideMobiUiIllustrationCdnSupport,
} from '@mobi/rwc-ui-components-ng-jslib';
import {
  AuthenticationService,
  provideAppAuthentication,
  withAuthenticatedUserTelemetry,
  withConfig,
  withImpersonation,
} from '@mobi/rwc-utils-authentication-jslib';
import {
  ConfigurationService,
  provideAzureCore,
} from '@mobi/rwc-utils-ng-jslib';

export interface CoreOptions {
  routes: Routes;
}

export function provideCore(
  options: CoreOptions
): (Provider | EnvironmentProviders)[] {
  return [
    provideAppAuthentication(
      withImpersonation({
        impersonableUsers: () => {
          const oauthService = inject(AuthenticationService);
          const http = inject(HttpClient);
          const config = inject(ConfigurationService);
          const oauthUserinfoUri = `${config.getSync(
            'oauthIssuerUrl'
          )}/protocol/openid-connect/userinfo`;

          return oauthService.getAccessToken().pipe(
            switchMap((t) =>
              http.get(oauthUserinfoUri, {
                headers: { Authorization: `Bearer ${t}` },
              })
            ),
            map(
              (userInfo: any) =>
                userInfo.interaction_profiles
                  ?.filter((profile: any) => !profile.current_profile)
                  .map((profile: any) => ({
                    userId: profile.cnumber,
                    label: `${profile.partner_fachnummer} ${
                      profile.partner_name || ''
                    }`,
                    cipId: profile.sub,
                  })) ?? []
            )
          );
        },
      }),
      withAuthenticatedUserTelemetry(),
      withConfig(() => ({
        autoUserInfo: true,
        customParamsAuthRequest: {
          ui_locales: 'it', // this one needs keycloak
          language: 'it', //this one is forwarded to nevis and hopefully sets lang there!?
          login_hint: 'mobi@mobi.ch',
          kc_idp_hint: 'wesb2c-int',
        },
      }))
    ),
    provideAzureCore(),
    provideAnimationsAsync(),
    provideZonelessChangeDetection(),
    provideRouter(
      options.routes,
      withRouterConfig({
        onSameUrlNavigation: 'reload',
      }),
      withComponentInputBinding(),
      withEnabledBlockingInitialNavigation(),
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'top',
      })
    ),
    provideMobiUiIconBundleSupport(),
    provideMobiUiIllustrationCdnSupport(),
  ];
}
